import { apiClient, getToken } from '../const';

/**
 * Request OTP for admin login.
 * POST /admin/auth/login
 * Body: { mobile: string }
 * 200: { success, message, data: { otp_sent: true } }
 * 401/403: { success: false, message, code } (e.g. ADMIN_NOT_FOUND, ACCOUNT_SUSPENDED)
 */
export const requestLogin = async (mobile) => {
  const res = await apiClient.post('/admin/auth/login', {
    mobile: String(mobile).trim(),
  });
  return res.data;
};

/**
 * Verify OTP and get token.
 * POST /admin/auth/verify-otp
 * Body: { mobile: string, otp: string }
 * 200: { success, message, data: { admin: {...}, token } }
 * 400/401: { success: false, message, code: OTP_EXPIRED | OTP_MAX_ATTEMPTS | OTP_INVALID | OTP_NOT_FOUND }
 */
export const verifyOtp = async (mobile, otp) => {
  const res = await apiClient.post('/admin/auth/verify-otp', {
    mobile: String(mobile).trim(),
    otp: String(otp).trim(),
  });
  return res.data;
};

/**
 * Register this browser for web push notifications.
 * POST /admin/devices/register
 * Body: { device_id, platform, device_type, token, user_agent }
 */
export const registerDevice = async (payload) => {
  const res = await apiClient.post('/admin/devices/register', payload, {
    skipBodyWrap: true,
  });
  return res.data;
};

/**
 * Unregister this browser from push (logout).
 * POST /admin/devices/unregister
 */
export const unregisterDevice = async (payload) => {
  const res = await apiClient.post('/admin/devices/unregister', payload, {
    skipBodyWrap: true,
  });
  return res.data;
};

/**
 * Get current admin (requires token).
 * GET /admin/auth/me
 * 200: { success, data: { admin } } or similar
 */
export const getMe = async () => {
  const res = await apiClient.get('/admin/auth/me');
  // console.log('getMe response:', res.data);
  return res.data;
};

/**
 * List users with optional search + status + date range + pagination.
 * POST /admin/users
 * Body: { limit, offset, search, status, status_type, from_date, to_date }
 */
export const getUsers = async ({
  search = '',
  status = '',
  status_type = '',
  from_date = '',
  to_date = '',
  start_date = '',
  end_date = '',
  limit = 10,
  offset = 0,
} = {}) => {
  const body = { limit, offset };
  if (search) body.search = search;
  if (status !== '' && status != null && status !== 'all') body.status = status;
  if (status_type) body.status_type = status_type;
  if (from_date) body.from_date = from_date;
  else if (start_date) body.from_date = start_date;
  if (to_date) body.to_date = to_date;
  else if (end_date) body.to_date = end_date;
  const res = await apiClient.post('/admin/users', body, { skipBodyWrap: true });
  // console.log('getUsers response:', res.data);
  return res.data;
};

/**
 * Get a single user by ID.
 * GET /admin/users/:id
 */
export const getUserById = async (id) => {
  const res = await apiClient.get(`/admin/users/${id}`);
  // console.log('getUserById response:', res.data);
  return res.data;
};
/**
 * Freeze a user account.
 * POST /admin/users/:id/freeze
 */
export const freezeUser = async (id) => {
  const res = await apiClient.post(`/admin/users/${id}/freeze`);
  // console.log('freezeUser response:', res.data);
  return res.data;
};

/**
 * Unfreeze a user account.
 * POST /admin/users/:id/unfreeze
 */
export const unfreezeUser = async (id) => {
  const res = await apiClient.post(`/admin/users/${id}/unfreeze`);
  // console.log('unfreezeUser response:', res.data);
  return res.data;
};

/**
 * Suspend a user account.
 * POST /admin/users/:id/suspend
 */
export const suspendUser = async (id) => {
  const res = await apiClient.post(`/admin/users/${id}/suspend`);
  // console.log('suspendUser response:', res.data);
  return res.data;
};

/**
 * Unsuspend a user account.
 * POST /admin/users/:id/unsuspend
 */
export const unsuspendUser = async (id) => {
  const res = await apiClient.post(`/admin/users/${id}/unsuspend`);
  // console.log('unsuspendUser response:', res.data);
  return res.data;
};

/**
 * Get user ledger entries (paginated).
 * POST /admin/users/:id/ledger
 * Body: { limit, offset }
 */
export const getUserLedger = async (id, { limit = 10, offset = 0 } = {}) => {
  const body = { limit, offset };
  const res = await apiClient.post(`/admin/users/${id}/ledger`, body, { skipBodyWrap: true });
  // console.log('getUserLedger response:', res.data);
  return res.data;
};

/**
 * Get user transactions (paginated).
 * POST /admin/users/:id/transactions
 * Body: { limit, offset }
 */
export const getUserTransactions = async (id, { limit = 10, offset = 0 } = {}) => {
  const body = { limit, offset };
  const res = await apiClient.post(`/admin/users/${id}/transactions`, body, { skipBodyWrap: true });
  // console.log('getUserTransactions response:', res.data);
  return res.data;
};

/**
 * List all ledger entries with optional filters + pagination.
 * POST /admin/ledger
 * Body: { limit, offset, wallet_id, entry_type }
 */
export const getLedger = async ({ wallet_id = '', entry_type = '', limit = 10, offset = 0 } = {}) => {
  const body = { limit, offset };
  if (wallet_id) body.wallet_id = wallet_id;
  if (entry_type) body.entry_type = entry_type;
  const res = await apiClient.post('/admin/ledger', body, { skipBodyWrap: true });
  // console.log('getLedger response:', res.data);
  return res.data;
};

/**
 * Get ledger for a specific wallet (paginated).
 * POST /admin/ledger/:wallet_id
 * Body: { limit, offset }
 */
export const getLedgerByWalletId = async (walletId, { limit = 10, offset = 0 } = {}) => {
  const body = { limit, offset };
  const res = await apiClient.post(`/admin/ledger/${walletId}`, body, { skipBodyWrap: true });
  return res.data;
};

/**
 * List all transactions with optional filters + pagination.
 * POST /admin/transactions
 * Body: { limit, offset, user_id, status, type, from_date, to_date }
 * Each row includes amount_inr (order/gold value), discount_inr (coins ₹),
 * paid_amount_inr (UPI cash; falls back to amount_inr when null).
 */
export const getTransactions = async ({
  status = '',
  type = '',
  user_id = '',
  from_date = '',
  to_date = '',
  limit = 10,
  offset = 0,
} = {}) => {
  const body = { limit, offset };
  if (status !== '' && status != null) body.status = status;
  if (type) body.type = type;
  if (user_id) body.user_id = user_id;
  if (from_date) body.from_date = from_date;
  if (to_date) body.to_date = to_date;
  const res = await apiClient.post('/admin/transactions', body, { skipBodyWrap: true });
  // console.log('getTransactions response:', res.data);
  return res.data;
};

/**
 * Get a single transaction by ID.
 * GET /admin/transactions/:id
 * Same money fields as list: amount_inr, discount_inr, paid_amount_inr.
 */
export const getTransactionById = async (id) => {
  const res = await apiClient.get(`/admin/transactions/${id}`);
  // console.log('getTransactionById response:', res.data);
  return res.data;
};

/**
 * List payouts with optional status filter + pagination.
 * POST /admin/payouts
 * Body: { limit, offset, status }
 */
export const getPayouts = async ({ status = '', limit = 10, offset = 0 } = {}) => {
  const body = { limit, offset };
  if (status) body.status = status;
  const res = await apiClient.post('/admin/payouts', body, { skipBodyWrap: true });
  // console.log('getPayouts response:', res.data);
  return res.data;
};

/**
 * Reprocess a failed transaction.
 * POST /admin/transactions/reprocess
 * Body: { transaction_id, order_id }
 * Requires Idempotency-Key header.
 */
export const reprocessTransaction = async (transactionId, orderId, idempotencyKey) => {
  const res = await apiClient.post(
    '/admin/transactions/reprocess',
    { transaction_id: transactionId, order_id: orderId },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return res.data;
};

/**
 * Retry a failed payout.
 * POST /admin/payouts/retry
 * Body: { payout_id }
 * Requires Idempotency-Key header.
 */
export const retryPayout = async (payoutId, idempotencyKey) => {
  const res = await apiClient.post(
    '/admin/payouts/retry',
    { payout_id: payoutId },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return res.data;
};

/**
 * List audit logs with optional filters + pagination.
 * POST /admin/audit-logs
 * Body: { limit, offset, actor_id, action, resource, actor_type, search, type }
 */
export const getAuditLogs = async ({
  actor_id = '',
  actor_type = '',
  action = '',
  resource = '',
  search = '',
  type = '',
  limit = 10,
  offset = 0,
} = {}) => {
  const body = { limit, offset };
  if (actor_id) body.actor_id = actor_id;
  if (actor_type) body.actor_type = actor_type;
  if (action) body.action = action;
  if (resource) body.resource = resource;
  if (search) body.search = search;
  if (type) body.type = type;
  const res = await apiClient.post('/admin/audit-logs', body, { skipBodyWrap: true });
  // console.log('getAuditLogs response:', res.data);
  return res.data;
};

/**
 * List all roles.
 * GET /admin/rbac/roles
 */
export const getRoles = async () => {
  const res = await apiClient.get('/admin/rbac/roles');
  return res.data;
};

/**
 * Create a new role.
 * POST /admin/rbac/roles
 * Body: { code, name, description }
 */
export const createRole = async (payload) => {
  const res = await apiClient.post('/admin/rbac/roles', payload, { skipBodyWrap: true });
  return res.data;
};

/**
 * List all permissions.
 * GET /admin/rbac/permissions
 */
export const getPermissions = async () => {
  const res = await apiClient.get('/admin/rbac/permissions');
  return res.data;
};

/**
 * Assign a role to an admin user.
 * POST /admin/rbac/roles/assign
 * Body: { admin_user_id, role_code }
 * Accepts either (payload) or (adminUserId, roleCode).
 */
export const assignRole = async (payloadOrId, roleCode) => {
  const body =
    typeof payloadOrId === 'object' && payloadOrId !== null
      ? {
          admin_user_id: Number(payloadOrId.admin_user_id ?? payloadOrId.admin_id),
          role_code: payloadOrId.role_code,
        }
      : {
          admin_user_id: Number(payloadOrId),
          role_code: roleCode,
        };
  const res = await apiClient.post('/admin/rbac/roles/assign', body, { skipBodyWrap: true });
  return res.data;
};

/**
 * List reward rules.
 * POST /admin/rewards/rules/list
 * Body: { limit, offset }
 */
export const getRewardRules = async ({ limit, offset } = {}) => {
  const body = {};
  if (limit != null) body.limit = limit;
  if (offset != null) body.offset = offset;
  const res = await apiClient.post('/admin/rewards/rules/list', body, { skipBodyWrap: true });
  // console.log('getRewardRules response:', res.data);
  return res.data;
};

/**
 * Create a new reward rule.
 * POST /admin/rewards/rules
 * Body: { code, description, type, value }
 */
export const createRewardRule = async (payload) => {
  const res = await apiClient.post('/admin/rewards/rules', payload);
  return res.data;
};

/**
 * Update an existing reward rule.
 * PUT /admin/rewards/rules/:id
 * Body: { code, description, type, value, is_active }
 */
export const updateRewardRule = async (id, payload) => {
  const res = await apiClient.put(`/admin/rewards/rules/${id}`, payload);
  return res.data;
};

/**
 * List admin investments overview.
 * POST /admin/investments
 * Body: { user_id }
 */
export const getInvestments = async ({ user_id = '' } = {}) => {
  const body = {};
  if (user_id) body.user_id = user_id;
  const res = await apiClient.post('/admin/investments', body, { skipBodyWrap: true });
  return res.data;
};

/**
 * Fetch grouped investment transactions for a user and type.
 * POST /admin/investments/transactions
 * Body: { user_id, type, source_ids }
 */
export const getInvestmentTransactions = async ({ user_id, type, source_ids }) => {
  const res = await apiClient.post(
    '/admin/investments/transactions',
    { user_id, type, source_ids },
    { skipBodyWrap: true },
  );
  return res.data;
};

/**
 * Pause an investment.
 * POST /admin/investments/pause
 * Body: { investment_id }
 * Requires Idempotency-Key header.
 */
export const pauseInvestment = async (investmentId, idempotencyKey) => {
  const res = await apiClient.post(
    '/admin/investments/pause',
    { investment_id: investmentId },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return res.data;
};

/**
 * Resume a paused investment.
 * POST /admin/investments/resume
 * Body: { investment_id }
 * Requires Idempotency-Key header.
 */
export const resumeInvestment = async (investmentId, idempotencyKey) => {
  const res = await apiClient.post(
    '/admin/investments/resume',
    { investment_id: investmentId },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  // console.log('runReconciliation response:', res.data);
  return res.data;
};

/**
 * List reconciliation runs with pagination.
 * POST /admin/reconciliation/runs
 * Body: { limit, offset, ... }
 */
export const getReconciliationRuns = async ({ limit = 100, offset = 0, ...rest } = {}) => {
  const body = { limit, offset, ...rest };
  const res = await apiClient.post('/admin/reconciliation/runs', body, { skipBodyWrap: true });
  // console.log('getReconciliationRuns response:', res.data);
  return res.data;
};

/**
 * Get all reconciliation exceptions (optionally filter by run_id).
 * POST /admin/reconciliation/exceptions
 * Body: { limit, offset, run_id, ... }
 */
export const getReconciliationExceptions = async (runIdOrParams) => {
  let body = {};
  if (typeof runIdOrParams === 'object' && runIdOrParams !== null) {
    body = { ...runIdOrParams };
  } else if (runIdOrParams) {
    body = { run_id: runIdOrParams };
  }
  const res = await apiClient.post('/admin/reconciliation/exceptions', body, { skipBodyWrap: true });
  // console.log('getReconciliationExceptions response:', res.data);
  return res.data;
};

/**
 * Trigger a new reconciliation run.
 * POST /admin/reconciliation/run
 * Requires Idempotency-Key header.
 */
export const runReconciliation = async (idempotencyKey) => {
  const res = await apiClient.post(
    '/admin/reconciliation/run',
    {},
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return res.data;
};

/**
 * List physical redemptions with optional status filter + pagination.
 * POST /admin/physical-redemptions
 * Body: { limit, offset }
 */
export const getPhysicalRedemptions = async ({ limit = 10, offset = 0 } = {}) => {
  const body = { limit, offset };
  const res = await apiClient.post('/admin/physical-redemptions', body, { skipBodyWrap: true });
  // console.log('getPhysicalRedemptions response:', res.data);
  return res.data;
};

/**
 * Lookup a redemption by code before completing it.
 * POST /admin/physical-redemptions/lookup
 * Body: { code }
 */
export const lookupRedemption = async (code) => {
  const body = typeof code === 'object' && code !== null ? code : { code };
  const res = await apiClient.post('/admin/physical-redemptions/lookup', body, { skipBodyWrap: true });
  // console.log('lookupRedemption response:', res.data);
  return res.data;
};

/**
 * Check device push status.
 * POST /admin/devices/push-status
 * Body: { device_id }
 */
export const getDevicePushStatus = async (deviceId) => {
  const body = typeof deviceId === 'object' && deviceId !== null ? deviceId : { device_id: deviceId };
  const res = await apiClient.post('/admin/devices/push-status', body, { skipBodyWrap: true });
  return res.data;
};

/**
 * Complete a physical redemption.
 * POST /admin/physical-redemptions/redeem
 * Body: { redemption_code, actual_quantity }
 * Requires Idempotency-Key header.
 */
export const redeemPhysical = async (redemptionCode, actualQuantity, idempotencyKey) => {
  const res = await apiClient.post(
    '/admin/physical-redemptions/redeem',
    { redemption_code: redemptionCode, actual_quantity: actualQuantity },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return res.data;
};

/**
 * List all admin users.
 * GET /admin/rbac/admin-users
 */
export const getAdminUsers = async ({ role_code = '', status = '', limit = 100, offset = 0 } = {}) => {
  const params = { limit, offset };
  if (role_code) params.role_code = role_code;
  if (status !== '') params.status = status;
  const res = await apiClient.get('/admin/rbac/admin-users', { params });
  return res.data;
};

/**
 * Get a single admin user by ID (includes permissions[]).
 * GET /admin/rbac/admin-users/:id
 */
export const getAdminUserById = async (id) => {
  const res = await apiClient.get(`/admin/rbac/admin-users/${id}`);
  return res.data;
};

/**
 * Update an admin user profile fields.
 * PUT /admin/rbac/admin-users/:id
 * Body: { name?, email?, status? }  // status 1|2 — does not change role
 */
export const updateAdminUser = async (id, payload) => {
  const res = await apiClient.put(`/admin/rbac/admin-users/${id}`, payload, {
    skipBodyWrap: true,
  });
  return res.data;
};

/**
 * Create a new admin user.
 * POST /admin/rbac/admin-users
 * Body: { name, mobile, role_code, email?, gender?, address?, status? }
 * There is no /admin/auth/register — admins are created via RBAC.
 */
export const createAdminUser = async (payload) => {
  const res = await apiClient.post('/admin/rbac/admin-users', payload, {
    skipBodyWrap: true,
  });
  return res.data;
};

/**
 * Save the full set of permissions for a role.
 * POST /admin/rbac/roles/permissions
 * Body: { role_code, permission_ids: number[] }
 */
export const saveRolePermissions = async (role_code, permission_ids) => {
  const res = await apiClient.post(
    '/admin/rbac/roles/permissions',
    { role_code, permission_ids },
    { skipBodyWrap: true },
  );
  return res.data;
};

// ── Dashboard ──────────────────────────────────────────────────────────

/**
 * Fetch all dashboard summary data.
 * POST /admin/dashboard
 * Body: { tx_type, tx_page, tx_limit }
 */
export const getDashboard = async (params = {}) => {
  const body = {};
  if (params.tx_type) body.tx_type = params.tx_type;
  if (params.tx_page != null) body.tx_page = params.tx_page;
  if (params.tx_limit != null) body.tx_limit = params.tx_limit;
  const res = await apiClient.post('/admin/dashboard', body, { skipBodyWrap: true });
  // console.log('getDashboard response:', res.data);
  return res.data;
};

// ── CMS (Blogs / FAQs / Legal / content types / videos) ────────────────
// JSDoc shapes (admin API, `success` + `data`):
/**
 * @typedef {Object} AdminCmsContentType
 * @property {number} id
 * @property {string} code
 * @property {string} label
 * @property {number} type_sort_order
 * @property {number|string} [status] published/draft per backend
 * @property {boolean} [published] legacy
 */
/**
 * @typedef {Object} AdminCmsFaq
 * @property {number} id
 * @property {number} content_type_id
 * @property {string} [content_type_code]
 * @property {string} [content_type_label]
 * @property {string} [faq_type] legacy slug
 * @property {string} question
 * @property {string} answer
 */
/**
 * @typedef {Object} AdminCmsVideo
 * @property {number} id
 * @property {number} content_type_id
 * @property {string} [content_type_code]
 * @property {string} [content_type_label]
 * @property {string} [video_type] legacy
 */

const rawRequestConfig = { skipBodyWrap: true };

export const getCmsBlogs = async ({ page, limit, search, status } = {}) => {
  const body = {};
  if (page != null) body.page = page;
  if (limit != null) body.limit = limit;
  if (search) body.search = search;
  if (status != null && status !== '') body.status = status;
  const res = await apiClient.post('/admin/cms/blogs/list', body, rawRequestConfig);
  return res.data;
};

export const getCmsBlogById = async (id) => {
  const res = await apiClient.get(`/admin/cms/blogs/${id}`);
  return res.data;
};

export const createCmsBlog = async (formData) => {
  const res = await apiClient.post('/admin/cms/blogs', formData, {
    ...rawRequestConfig,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const updateCmsBlog = async (id, formData) => {
  const res = await apiClient.put(`/admin/cms/blogs/${id}`, formData, {
    ...rawRequestConfig,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const deleteCmsBlog = async (id) => {
  const res = await apiClient.delete(`/admin/cms/blogs/${id}`);
  return res.data;
};

/**
 * Admin FAQ list (paginated).
 * POST /admin/cms/faqs/list
 * Body: { page, limit, type, status }
 */
export const getCmsFaqs = async ({
  page = 1,
  limit = 10,
  content_type_id = '',
  content_type_code = '',
  type = '',
  faq_type = '',
  status = '',
} = {}) => {
  const body = { page, limit };
  const code = String(content_type_code || type || faq_type || '').trim();
  if (code) {
    body.type = code;
  }
  if (content_type_id !== '' && content_type_id != null) {
    const n = Number(content_type_id);
    if (Number.isFinite(n) && n > 0) body.content_type_id = n;
  }
  if (status !== '' && status != null) body.status = status;
  const res = await apiClient.post('/admin/cms/faqs/list', body, rawRequestConfig);
  return res.data;
};

export const createCmsFaq = async (payload) => {
  const res = await apiClient.post('/admin/cms/faqs', payload, rawRequestConfig);
  return res.data;
};

export const updateCmsFaq = async (id, payload) => {
  const res = await apiClient.put(`/admin/cms/faqs/${id}`, payload, rawRequestConfig);
  return res.data;
};

export const deleteCmsFaq = async (id) => {
  const res = await apiClient.delete(`/admin/cms/faqs/${id}`);
  return res.data;
};

export const getCmsLegalDocuments = async ({ type = '', status = '' } = {}) => {
  const body = {};
  if (type) body.type = type;
  if (status !== '' && status != null) body.status = status;
  const res = await apiClient.post('/admin/cms/legal/list', body, rawRequestConfig);
  return res.data;
};

export const createCmsLegalDocument = async (payload) => {
  const res = await apiClient.post('/admin/cms/legal', payload, rawRequestConfig);
  return res.data;
};

export const updateCmsLegalDocument = async (id, payload) => {
  const res = await apiClient.put(`/admin/cms/legal/${id}`, payload, rawRequestConfig);
  return res.data;
};

export const deleteCmsLegalDocument = async (id) => {
  const res = await apiClient.delete(`/admin/cms/legal/${id}`);
  return res.data;
};

/** POST /admin/cms/content-types/list — body: { status } */
export const getCmsContentTypes = async ({ status = '' } = {}) => {
  const body = {};
  if (status) body.status = status;
  const res = await apiClient.post('/admin/cms/content-types/list', body, rawRequestConfig);
  return res.data;
};

/** POST /admin/cms/content-types — { label, code?, type_sort_order?, published? } */
export const createCmsContentType = async (payload) => {
  const res = await apiClient.post('/admin/cms/content-types', payload, rawRequestConfig);
  return res.data;
};

/** PUT /admin/cms/content-types/:code */
export const updateCmsContentType = async (code, payload) => {
  const res = await apiClient.put(
    `/admin/cms/content-types/${encodeURIComponent(code)}`,
    payload,
    rawRequestConfig,
  );
  return res.data;
};

/** PUT /admin/cms/content-types/reorder — items: [{ content_type_id|id, type_sort_order }] (code legacy) */
export const reorderCmsContentTypes = async (payload) => {
  const res = await apiClient.put('/admin/cms/content-types/reorder', payload, rawRequestConfig);
  return res.data;
};

export const deleteCmsContentType = async (code) => {
  const res = await apiClient.delete(`/admin/cms/content-types/${encodeURIComponent(code)}`);
  return res.data;
};

/**
 * POST /admin/cms/videos/list
 * Body: { page, limit, type, status }
 */
export const getCmsVideos = async ({
  page = 1,
  limit = 10,
  type = '',
  content_type_id = '',
  status = '',
} = {}) => {
  const body = { page, limit };
  if (content_type_id !== '' && content_type_id != null) {
    const n = Number(content_type_id);
    if (Number.isFinite(n) && n > 0) body.content_type_id = n;
  }
  if (type) body.type = type;
  if (status !== '' && status != null) body.status = status;
  const res = await apiClient.post('/admin/cms/videos/list', body, rawRequestConfig);
  return res.data;
};

/** POST /admin/cms/videos */
export const createCmsVideo = async (payload) => {
  const res = await apiClient.post('/admin/cms/videos', payload, rawRequestConfig);
  return res.data;
};

/** PUT /admin/cms/videos/:id */
export const updateCmsVideo = async (id, payload) => {
  const res = await apiClient.put(`/admin/cms/videos/${id}`, payload, rawRequestConfig);
  return res.data;
};

export const deleteCmsVideo = async (id) => {
  const res = await apiClient.delete(`/admin/cms/videos/${id}`);
  return res.data;
};

// ── Support tickets (admin inbox) ───────────────────────────────────────

const rawJsonConfig = { skipBodyWrap: true };

/**
 * List support tickets (queue).
 * POST /admin/support/tickets
 * Body: list filters
 */
export const listSupportTickets = async (filters = {}) => {
  const res = await apiClient.post('/admin/support/tickets', filters, rawJsonConfig);
  return res.data;
};

/**
 * Ticket detail + user info.
 * GET /admin/support/tickets/:ticketId
 */
export const getSupportTicket = async (ticketId) => {
  const res = await apiClient.get(`/admin/support/tickets/${ticketId}`);
  return res.data;
};

/**
 * Chat history (paginated).
 * POST /admin/support/tickets/:ticketId/messages/list
 * Body: pagination/filters
 */
export const getSupportTicketMessages = async (ticketId, filters = {}) => {
  const res = await apiClient.post(
    `/admin/support/tickets/${ticketId}/messages/list`,
    filters,
    rawJsonConfig,
  );
  return res.data;
};

/**
 * Admin reply.
 * POST /admin/support/tickets/:ticketId/messages
 */
export const postSupportTicketMessage = async (ticketId, body) => {
  const res = await apiClient.post(
    `/admin/support/tickets/${ticketId}/messages`,
    body,
    rawJsonConfig,
  );
  return res.data;
};

/**
 * Update ticket (status, priority, assignee, notes).
 * PATCH /admin/support/tickets/:ticketId
 */
export const patchSupportTicket = async (ticketId, body) => {
  const res = await apiClient.patch(`/admin/support/tickets/${ticketId}`, body, rawJsonConfig);
  return res.data;
};

/**
 * Mark messages read up to id.
 * POST /admin/support/tickets/:ticketId/read
 */
export const postSupportTicketRead = async (ticketId, upToMessageId) => {
  const res = await apiClient.post(
    `/admin/support/tickets/${ticketId}/read`,
    { up_to_message_id: upToMessageId },
    rawJsonConfig,
  );
  return res.data;
};

/**
 * Reopen a closed ticket.
 * POST /admin/support/tickets/:ticketId/reopen
 */
export const postSupportTicketReopen = async (ticketId, reason) => {
  const res = await apiClient.post(
    `/admin/support/tickets/${ticketId}/reopen`,
    { reason: String(reason ?? '').trim() },
    rawJsonConfig,
  );
  return res.data;
};

/**
 * Fetch users who used a given referral code.
 * POST /admin/referrals/users
 * Body: { referral_code, from_date?, to_date?, limit?, offset? }
 * 200: { success, data: { referral_code, from_date, to_date, total_count, users[] } }
 */
export const getReferralUsers = async ({
  referral_code,
  from_date,
  to_date,
  limit = 100,
  offset = 0,
} = {}) => {
  const body = { referral_code, limit, offset };
  if (from_date) body.from_date = from_date;
  if (to_date) body.to_date = to_date;
  const res = await apiClient.post('/admin/referrals/users', body, { skipBodyWrap: true });
  return res.data;
};

export { getToken };
